<template>
  <div class="person-select__person-list">
    <div class="person-select__search">
      <el-input v-model.trim="keyword" clearable placeholder="输入人员名称" @input="handleSearch">
        <template #prefix>
          <i class="icon-search-line person-select__search-icon t-iconfont" />
        </template>
      </el-input>
    </div>

    <div v-if="departmentBreadcrumbs.length" class="person-select__selected-department">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item v-for="(item, index) in departmentBreadcrumbs" :key="item.pid">
          <button
            :disabled="index === departmentBreadcrumbs.length - 1"
            type="button"
            @click="handleBreadcrumbClick(item, index)"
          >
            {{ item.text }}
          </button>
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div v-loading="loading" class="person-select__list">
      <el-empty
        v-if="!departmentList.length && !userList.length && !loading"
        description="暂无部门或人员"
        :image-size="80"
      />
      <div
        v-for="item in departmentList"
        :key="item.pid"
        class="person-select__department-item person-select__item"
        @click="handleDepartmentSelect(item)"
      >
        <span class="person-select__department-avatar">
          <i class="icon-node-tree person-select__department-icon t-iconfont" />
        </span>
        <span class="person-select__department-name">{{ item.text }}</span>
        <span class="person-select__department-arrow">
          <i class="icon-arrow-right-s-line person-select__department-arrow-icon t-iconfont" />
        </span>
      </div>
      <div
        v-if="userList.length"
        class="person-select__item person-select__select-all"
        @click="emits('select-all', userList, !isAllUsersSelected)"
      >
        <el-checkbox
          :indeterminate="isUserSelectionIndeterminate"
          :model-value="isAllUsersSelected"
          @change="emits('select-all', userList, $event)"
          @click.stop
        >
          全选
        </el-checkbox>
      </div>
      <div
        v-for="item in userList"
        :key="item.userId"
        class="person-select__item"
        :class="{ 'person-select__item--checked': isChecked(item) }"
        @click="emits('select', item)"
      >
        <el-checkbox
          :model-value="isChecked(item)"
          @change="() => {}"
          @click.stop="emits('select', item)"
        />
        <div class="person-select__avatar">
          <el-avatar
            v-if="item.avatar && item.avatar !== '/images/default.jpg'"
            shape="square"
            :size="24"
            :src="item.avatar"
          />
          <span v-else>{{ item.name.slice(-2) }}</span>
        </div>
        <span class="person-select__name">{{ item.name }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { getDepartmentUserTree } from '@/api'
import { geuUserList } from '@/api/ai-board'

defineOptions({
  name: 'PersonList'
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  isLicense: {
    type: Number,
    default: 0
  },
  selectedList: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['select', 'select-all'])

const keyword = ref('')
const loading = ref(false)
const departmentList = ref([])
const userList = ref([])
const rootDepartment = ref(null)
const departmentBreadcrumbs = ref([])
let searchTimer = null
const isSameId = (left, right) => String(left) === String(right)

const isChecked = (item) =>
  props.selectedList.some(
    (selected) => (selected.property || 'user') === 'user' && isSameId(selected.id, item.userId)
  )
const selectedUserCount = computed(() => userList.value.filter((item) => isChecked(item)).length)
const isAllUsersSelected = computed(
  () => userList.value.length > 0 && selectedUserCount.value === userList.value.length
)
const isUserSelectionIndeterminate = computed(
  () => selectedUserCount.value > 0 && selectedUserCount.value < userList.value.length
)

const setCurrentDepartment = (department) => {
  departmentList.value = department?.nodes || []
  userList.value = department?.userList || []
}

const searchUserList = async () => {
  try {
    const res = await geuUserList({
      page: 1,
      pageSize: 99999,
      nameLike: keyword.value,
      isLicense: props.isLicense
    })
    userList.value = res?.result?.userList || []
  } catch (error) {
    console.error(error)
    userList.value = []
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  clearTimeout(searchTimer)
  if (!keyword.value) {
    const currentDepartment =
      departmentBreadcrumbs.value[departmentBreadcrumbs.value.length - 1] || rootDepartment.value
    setCurrentDepartment(currentDepartment)
    loading.value = false
    return
  }

  loading.value = true
  departmentList.value = []
  userList.value = []
  searchTimer = setTimeout(searchUserList, 500)
}

const getOrganizationTree = async () => {
  loading.value = true
  try {
    const res = await getDepartmentUserTree({ isLicense: props.isLicense })
    rootDepartment.value = res?.result?.departmentTree?.[0] || null
    setCurrentDepartment(rootDepartment.value)
  } catch (error) {
    console.error(error)
    rootDepartment.value = null
    departmentList.value = []
    userList.value = []
  } finally {
    loading.value = false
  }
}

const handleDepartmentSelect = (item) => {
  if (item.disabled || !item.pid) return
  const currentPath = departmentBreadcrumbs.value.length
    ? departmentBreadcrumbs.value
    : rootDepartment.value
      ? [rootDepartment.value]
      : []

  keyword.value = ''
  departmentBreadcrumbs.value = [...currentPath, item]
  setCurrentDepartment(item)
}

const handleBackToDepartment = () => {
  departmentBreadcrumbs.value = []
  keyword.value = ''
  setCurrentDepartment(rootDepartment.value)
}

const handleBreadcrumbClick = (item, index) => {
  if (index === departmentBreadcrumbs.value.length - 1) return
  if (index === 0) {
    handleBackToDepartment()
    return
  }

  departmentBreadcrumbs.value = departmentBreadcrumbs.value.slice(0, index + 1)
  keyword.value = ''
  setCurrentDepartment(item)
}

const reset = () => {
  clearTimeout(searchTimer)
  keyword.value = ''
  departmentList.value = []
  userList.value = []
  rootDepartment.value = null
  departmentBreadcrumbs.value = []
  getOrganizationTree()
}

watch(
  () => props.show,
  (visible) => {
    if (!visible) return
    reset()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
})
</script>

<style scoped lang="less">
.person-select {
  &__person-list {
    /* 拓展色/紫罗兰/Violets-4 */
    --person-department-avatar: #a871e3;
    /* 文字/Text-4 强调正文标题 */
    --person-department-name: #323233;
    /* 文字/Text-2 次要信息 */
    --person-department-secondary: #969799;

    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 16px 16px 0;
    height: 100%;
  }

  &__search-icon {
    color: var(--person-department-secondary);
  }

  &__department-item {
    display: flex;
    flex: 1;
    align-items: center;
    box-sizing: border-box;
    min-width: 0;
    height: 40px;
    padding: 8px;
    cursor: pointer;
  }

  &__department-avatar {
    display: flex;
    flex: 0 0 24px;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: var(--person-department-avatar);
    border-radius: 8px;
  }

  &__department-icon {
    color: #ffffff;
    font-size: 12px;
    line-height: 12px;
  }

  &__department-name {
    flex: 1;
    min-width: 0;
    margin-left: 8px;
    overflow: hidden;
    color: var(--person-department-name);
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__department-arrow {
    display: flex;
    flex: 0 0 20px;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-left: 8px;
    color: var(--person-department-secondary);
  }

  &__department-arrow-icon {
    font-size: 20px;
    line-height: 20px;
  }

  &__selected-department {
    display: flex;
    flex-shrink: 0;
    align-items: flex-start;
    margin-top: 12px;

    :deep(.el-breadcrumb) {
      display: flex;
      flex-wrap: wrap;
      min-width: 0;
      width: 100%;
      font-family: 'PingFang SC', sans-serif;
      font-size: 14px;
      line-height: 20px;
      row-gap: 4px;
    }

    :deep(.el-breadcrumb__item) {
      display: flex;
      flex: 0 0 auto;
      min-width: 0;
      max-width: 100%;
    }

    :deep(.el-breadcrumb__inner) {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      color: var(--person-department-secondary);
      font-weight: 400;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    :deep(.el-breadcrumb__inner button) {
      display: block;
      max-width: 100%;
      padding: 0;
      overflow: hidden;
      color: inherit;
      font: inherit;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
      background: transparent;
      border: 0;
    }

    :deep(.el-breadcrumb__inner button:disabled) {
      color: var(--person-department-name);
      cursor: default;
    }

    :deep(.el-breadcrumb__inner button:not(:disabled):hover) {
      color: #7a52f4;
    }

    :deep(.el-breadcrumb__separator) {
      flex: 0 0 16px;
      width: 16px;
      margin: 0 4px;
      color: var(--person-department-secondary);
      font-weight: 400;
      text-align: center;
    }
  }

  &__list {
    flex: 1;
    margin-top: 12px;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    height: 40px;
    padding: 8px;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #f7f8fa;
    }

    :deep(.el-checkbox) {
      height: 14px;
      margin-right: 8px;
    }

    :deep(.el-checkbox__inner) {
      width: 14px;
      height: 14px;
    }

    :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
      background-color: #7a52f4;
      border-color: #7a52f4;
    }
  }

  &__select-all {
    :deep(.el-checkbox) {
      margin-right: 0;
    }

    :deep(.el-checkbox__label) {
      padding-left: 8px;
      color: #323233;
      font-size: 14px;
    }
  }

  &__avatar {
    display: flex;
    flex: 0 0 24px;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-right: 8px;
    overflow: hidden;
    color: #ffffff;
    font-size: 8px;
    font-weight: 600;
    background: #a871e3;
    border-radius: 6px;

    :deep(.el-avatar--square) {
      border-radius: 6px;
    }
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: #323233;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
